import React, { useState } from 'react';
import { useLogin, useToast } from '@/hooks';
import Button from '@/components/common/Button/Button';
import Input from '@/components/common/Input/Input';
import styles from './AuthForm.module.css';

/**
 * LoginForm Component
 * Login form with validation and toast notifications
 */
const LoginForm = ({ onSwitchToSignup }) => {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  
  const {
    isLoading,
    showTwoFactorInput,
    setShowTwoFactorInput,
    twoFactorCode,
    setTwoFactorCode,
    handleLoginSubmit,
    handleTwoFactorSubmit,
    showError,
    showInfo,
  } = useLogin();

  const handleTwoFactorFormSubmit = async (e) => {
    e.preventDefault();
    await handleTwoFactorSubmit(twoFactorCode);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.username.trim()) {
      newErrors.username = 'Username or email is required';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      showError('Please fix the errors in the form');
      return;
    }

    const { username, password } = formData;

    if (!(username && password)) {
      showError('Please enter username and password');
      return;
    }

    await handleLoginSubmit(username, password);
  };

  const handleForgotPassword = (e) => {
    e.preventDefault();
    showInfo('Forgot password feature coming soon!');
  };

  if (showTwoFactorInput) {
    return (
      <form onSubmit={handleTwoFactorFormSubmit} className={styles.form}>
        <div className={styles.title}>Two-Factor Authentication</div>
        <p className={styles.subtitle}>Please enter your verification code.</p>

        <Input
          label="Verification Code"
          type="text"
          name="twoFactorCode"
          value={twoFactorCode}
          onChange={(e) => setTwoFactorCode(e.target.value)}
          placeholder="Enter verification code"
          disabled={isLoading}
          required
        />

        <Button
          type="submit"
          variant="primary"
          size="lg"
          fullWidth
          disabled={isLoading}
        >
          {isLoading ? 'Verifying...' : 'Verify Code'}
        </Button>
      </form>
    );
  }

  return (
    <form onSubmit={handleSubmit} className={styles.form}>
      <div className={styles.title}>Sign In</div>
      <p className={styles.subtitle}>Welcome back! Please sign in to your account.</p>

      <Input
        label="Username or Email"
        type="text"
        name="username"
        value={formData.username}
        onChange={handleInputChange}
        placeholder="Enter your username or email"
        error={errors.username}
        disabled={isLoading}
        required
      />

      <div className={styles.passwordInputWrapper}>
        <Input
          label="Password"
          type={showPassword ? 'text' : 'password'}
          name="password"
          value={formData.password}
          onChange={handleInputChange}
          placeholder="Enter your password"
          error={errors.password}
          disabled={isLoading}
          required
        />
        <button
          type="button"
          className={styles.passwordToggle}
          onClick={() => setShowPassword(!showPassword)}
          disabled={isLoading}
          aria-label={showPassword ? 'Hide password' : 'Show password'}
        >
          {showPassword ? (
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
              <line x1="1" y1="1" x2="23" y2="23" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
          )}
        </button>
      </div>

      <div className={styles.forgotPassword}>
        <a href="#" onClick={handleForgotPassword}>
          Forgot your password?
        </a>
      </div>

      <Button
        type="submit"
        variant="primary"
        size="lg"
        fullWidth
        disabled={isLoading}
      >
        {isLoading ? 'Signing in...' : 'Sign In'}
      </Button>

      <div className={styles.divider}>
        <span>or continue with</span>
      </div>

      <div className={styles.socialLogin}>
        <button type="button" className={styles.socialButton} disabled={isLoading}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
          Google
        </button>
      </div>

      <div className={styles.switchAuth}>
        Don't have an account?{' '}
        <a href="#" onClick={(e) => { e.preventDefault(); onSwitchToSignup(); }}>
          Sign up
        </a>
      </div>
    </form>
  );
};

export default LoginForm;
